// DGC genesis issuance — public transparency page

import { useSuiClientQuery } from '@mysten/dapp-kit';
import clsx from 'clsx';
import { useEffect, useMemo, useState } from 'react';

import { PageLayout } from '~/components/Layout/PageLayout';
import {
	CpcPageHero,
	CpcSection,
	CpcSectionTitle,
	cpcPageContentClass,
} from '~/components/cpc/CpcPageHero';
import {
	CPC_GENESIS_ALLOCATION_ROWS,
	CPC_GENESIS_TOTAL_SUPPLY,
	CPC_GENESIS_VALIDATOR_COUNT,
	CPC_GENESIS_VALIDATOR_STAKE_EACH,
	CPC_STAKE_LOCK_EPOCHS,
	type CpcGenesisPoolRow,
	formatGenesisDgc,
	mergeGenesisPoolAddresses,
} from '~/config/cpcGenesisTransparency';
import { useT } from '~/i18n/LocaleContext';
import { AddressLink } from '~/ui/InternalLink';
import { CPC_SYMBOL } from '~/utils/cpcCoin';
import { tryFormatCpcAddress } from '~/utils/cpcAddress';

function sumCheckOk(rows: CpcGenesisPoolRow[]): boolean {
	const sum = rows.reduce((a, r) => a + r.amount, 0);
	return sum === CPC_GENESIS_TOTAL_SUPPLY;
}

export default function CpcGenesisPage() {
	const t = useT();
	const { data: systemState, isPending } = useSuiClientQuery('getLatestSuiSystemState');
	const liveValidators = systemState?.activeValidators?.length ?? null;
	const validatorsMatch =
		liveValidators != null && liveValidators === CPC_GENESIS_VALIDATOR_COUNT;

	const [addressById, setAddressById] = useState<Record<string, string>>({});

	useEffect(() => {
		let cancelled = false;
		(async () => {
			try {
				const res = await fetch('/cpc-genesis-allocations.json', { cache: 'no-store' });
				if (!res.ok) return;
				const doc = await res.json();
				const map: Record<string, string> = {};
				for (const p of doc?.pools ?? []) {
					if (p?.id && p?.address) map[String(p.id)] = String(p.address).trim();
				}
				// system subsidy lands at 0x0
				if (!map.stake_subsidy_fund) {
					map.stake_subsidy_fund = '0x0';
				}
				if (!cancelled) setAddressById(map);
			} catch {
				/* optional file */
			}
		})();
		return () => {
			cancelled = true;
		};
	}, []);

	const rows = useMemo(
		() => mergeGenesisPoolAddresses(CPC_GENESIS_ALLOCATION_ROWS, addressById),
		[addressById],
	);

	const poolName = (row: CpcGenesisPoolRow) => t(`genesis.pool.${row.id}`, row.name);
	const poolNote = (row: CpcGenesisPoolRow) => {
		if (row.id === 'validators') {
			return t('genesis.pool.validatorsNote')
				.replace('{count}', String(CPC_GENESIS_VALIDATOR_COUNT))
				.replace('{each}', CPC_GENESIS_VALIDATOR_STAKE_EACH.toLocaleString('en-US'))
				.replace('{epochs}', String(CPC_STAKE_LOCK_EPOCHS));
		}
		return t(`genesis.pool.${row.id}Note`, row.note);
	};
	const vestingLabelOf = (v: CpcGenesisPoolRow['vesting']) => {
		const key = `genesis.vesting.${v}`;
		if (v === 'self_stake') {
			return t(key).replace('{epochs}', String(CPC_STAKE_LOCK_EPOCHS));
		}
		return t(key);
	};
	const vestingHintOf = (v: CpcGenesisPoolRow['vesting']) => t(`genesis.hint.${v}`);

	return (
		<PageLayout
			content={
				<div className={cpcPageContentClass}>
					<CpcPageHero
						title={t('genesis.title')}
						subtitle={t('genesis.subtitle')
							.replace('{total}', formatGenesisDgc(CPC_GENESIS_TOTAL_SUPPLY))
							.replace('{symbol}', CPC_SYMBOL)}
						mobileSubtitle={t('genesis.mobileSubtitle')}
					/>

					<CpcSection>
						<CpcSectionTitle>{t('genesis.supplyCheck')}</CpcSectionTitle>
						<div className="grid gap-3 sm:grid-cols-3">
							<div className="rounded-xl border border-white/10 bg-white/5 p-4">
								<p className="text-xs font-medium text-[#94a3b8]">{t('genesis.totalSupply')}</p>
								<p className="mt-2 text-xl font-bold tabular-nums text-white">
									{formatGenesisDgc(CPC_GENESIS_TOTAL_SUPPLY)}
								</p>
							</div>
							<div className="rounded-xl border border-white/10 bg-white/5 p-4">
								<p className="text-xs font-medium text-[#94a3b8]">{t('genesis.tableSum')}</p>
								<p className="mt-2 text-xl font-bold tabular-nums text-white">
									{sumCheckOk(rows) ? (
										<>
											{t('genesis.equalsTotal')} <span className="text-[#4ade80]">✓</span>
										</>
									) : (
										<>
											{t('genesis.notEqualsTotal')} <span className="text-[#f87171]">✗</span>
										</>
									)}
								</p>
							</div>
							<div className="rounded-xl border border-white/10 bg-white/5 p-4">
								<p className="text-xs font-medium text-[#94a3b8]">{t('genesis.liveValidators')}</p>
								<p
									className={clsx(
										'mt-2 text-xl font-bold tabular-nums',
										isPending ? 'text-[#94a3b8]' : 'text-white',
									)}
								>
									{isPending
										? t('genesis.reading')
										: liveValidators == null
											? t('genesis.unavailable')
											: `${liveValidators} / ${t('genesis.expected').replace('{count}', String(CPC_GENESIS_VALIDATOR_COUNT))}`}
								</p>
								<p
									className={clsx(
										'mt-1 text-xs',
										isPending
											? 'text-[#64748b]'
											: validatorsMatch
												? 'text-[#4ade80]'
												: 'text-[#fbbf24]',
									)}
								>
									{t('genesis.compareConfig')}
								</p>
							</div>
						</div>
					</CpcSection>

					<CpcSection>
						<CpcSectionTitle>{t('genesis.allocationDetail')}</CpcSectionTitle>
						<p className="mb-3 text-xs leading-relaxed text-[#94a3b8]">
							{t('genesis.allocationIntro')}
							<strong className="font-medium text-[#cbd5e1]">{t('genesis.linear10yStrong')}</strong>
							{t('genesis.allocationIntro2')}
							<code className="mx-0.5 text-[11px] text-[#cbd5e1]">linear::Wallet</code>
							{t('genesis.allocationIntro3')}
						</p>
						<div className="overflow-x-auto">
							<table className="w-full min-w-[720px] border-collapse text-left text-sm">
								<thead>
									<tr className="border-b border-white/10 text-[#aeb9d0]">
										<th className="py-2 pr-3 font-medium">{t('genesis.colPurpose')}</th>
										<th className="py-2 pr-3 font-medium tabular-nums">{t('genesis.colAmount')}</th>
										<th className="py-2 pr-3 font-medium tabular-nums">{t('genesis.colPct')}</th>
										<th className="py-2 pr-3 font-medium">{t('genesis.colVesting')}</th>
										<th className="py-2 font-medium">{t('genesis.colAddress')}</th>
									</tr>
								</thead>
								<tbody>
									{rows.map((row) => (
										<tr key={row.id} className="border-b border-white/5 text-[#e2e8f0]">
											<td className="py-3 pr-3 font-medium text-white">
												{poolName(row)}
												<p className="mt-0.5 text-xs font-normal text-[#64748b]">{poolNote(row)}</p>
											</td>
											<td className="py-3 pr-3 tabular-nums text-white">
												{row.amount.toLocaleString('en-US')}
											</td>
											<td className="py-3 pr-3 tabular-nums text-white">{row.pct}%</td>
											<td className="py-3 pr-3">
												<p
													className={
														row.vesting === 'linear_10y'
															? 'font-medium text-[#38BDF8]'
															: row.vesting === 'none'
																? 'font-medium text-[#4ade80]'
																: row.vesting === 'self_stake'
																	? 'font-medium text-[#fbbf24]'
																	: 'font-medium text-[#aeb9d0]'
													}
												>
													{vestingLabelOf(row.vesting)}
												</p>
												<p className="mt-0.5 max-w-[14rem] text-[11px] leading-snug text-[#64748b]">
													{vestingHintOf(row.vesting)}
												</p>
											</td>
											<td className="py-3">
												{row.address ? (
													<AddressLink
														address={row.address}
														label={tryFormatCpcAddress(row.address)}
														title={row.address}
														noTruncate
														className="break-all text-[#38BDF8] hover:underline"
													/>
												) : row.id === 'validators' ? (
													<span className="text-[#64748b]">{t('genesis.seeStakePage')}</span>
												) : (
													<span className="text-[#64748b]">{t('genesis.pendingSync')}</span>
												)}
											</td>
										</tr>
									))}
								</tbody>
								<tfoot>
									<tr className="text-white">
										<td className="pt-3 pr-3 font-semibold">{t('genesis.total')}</td>
										<td className="pt-3 pr-3 font-semibold tabular-nums">
											{CPC_GENESIS_TOTAL_SUPPLY.toLocaleString('en-US')}
										</td>
										<td className="pt-3 pr-3 font-semibold tabular-nums">100%</td>
										<td colSpan={2} />
									</tr>
								</tfoot>
							</table>
						</div>
						<div className="mt-4 space-y-1.5">
							{rows.map((row) => (
								<div key={`bar-${row.id}`} className="flex items-center gap-3 text-xs">
									<span className="w-28 shrink-0 truncate text-[#aeb9d0]">{poolName(row)}</span>
									<div className="h-2 flex-1 overflow-hidden rounded-full bg-white/5">
										<div
											className="h-full rounded-full bg-[#38BDF8]/80"
											style={{ width: `${Math.max(row.pct, 0.4)}%` }}
										/>
									</div>
									<span className="w-14 shrink-0 text-right tabular-nums text-[#94a3b8]">
										{row.pct}%
									</span>
								</div>
							))}
						</div>
						<p className="mt-4 text-xs text-[#64748b]">{t('genesis.footerHint')}</p>
					</CpcSection>
				</div>
			}
		/>
	);
}
